/**
 * A visit's result — PLAN §3.3, the one inspection rule the console applies itself.
 *
 *   Held             any line of the visit is Must fix (fix_now);
 *   Passed – fix soon  else any line is Fix soon;
 *   Passed           else;
 *   Notes only       a legacy row (kind NULL), which has no lines and no verdict.
 *
 * The result comes only from the visit's own lines, so it never changes after the visit. The
 * functions compute the same rule server-side (get_inspection_board, get_contract_inspections,
 * record_inspection); this copy serves the screens that read the tables directly — the Records view
 * and the vehicle page's Compliance card — and the certificate preview of lines not yet sent.
 * Attendance, condition, the hold and the release are NEVER worked out here: they are the server's.
 */

import type { Grade, InspectionKind, VisitResult } from '@/lib/inspections';

export function visitResult(kind: InspectionKind | null | undefined, grades: readonly Grade[]): VisitResult {
    if (kind === null || kind === undefined) return 'notes_only';
    if (grades.includes('fix_now')) return 'held';
    if (grades.includes('fix_soon')) return 'passed_fix_soon';
    return 'passed';
}

export function gradeCounts(grades: readonly Grade[]): { fixNow: number; fixSoon: number } {
    let fixNow = 0;
    let fixSoon = 0;
    for (const g of grades) {
        if (g === 'fix_now') fixNow += 1;
        else if (g === 'fix_soon') fixSoon += 1;
    }
    return { fixNow, fixSoon };
}

/**
 * The certificate preview's banner (PLAN §6 step 8) for the lines about to be sent: "Passed",
 * "Passed – 2 to fix soon" or "Held – 1 must be fixed before release".
 */
export function resultBanner(grades: readonly Grade[]): string {
    const { fixNow, fixSoon } = gradeCounts(grades);
    if (fixNow > 0) return `Held – ${fixNow} must be fixed before release`;
    if (fixSoon > 0) return `Passed – ${fixSoon} to fix soon`;
    return 'Passed';
}

/**
 * A re-check's banner: "Released" once no Must fix remains on a car that was held (PLAN §6,
 * re-check). `heldBefore` is the server's condition of the car as read, never a client guess.
 */
export function recheckBanner(heldBefore: boolean, grades: readonly Grade[]): string {
    const { fixNow } = gradeCounts(grades);
    if (heldBefore && fixNow === 0) return 'Released';
    return resultBanner(grades);
}

/**
 * The success screen's instruction (PLAN §6 step 9), from the server's `held` and the Must-fix
 * lines just sent. A car with no contract in progress has no driver to release it to.
 */
export function successHeadline(held: boolean, fixNowSent: number, hasDriver: boolean): string {
    if (held) {
        return fixNowSent > 0 ? `Keep the car: ${fixNowSent} must be fixed` : 'Keep the car: it is still held';
    }
    return hasDriver ? 'Release the car to the driver' : 'Nothing must be fixed: the car is not held';
}
